export type Point = { x: number; y: number };
export type Layer = {
  radii: readonly number[];
  frequencies: readonly number[];
  phase: number;
  color: string;
  start: number;
  finish: number;
};

// Every frequency is congruent to 1 modulo 12: exact 12-fold symmetry.
export const layers: readonly Layer[] = [
  {
    radii: [185, 150, 32],
    frequencies: [1, -11, 13],
    phase: -Math.PI / 2,
    color: "#79f5e7",
    start: 0,
    finish: 9.6,
  },
  {
    radii: [238, 112, 38],
    frequencies: [1, -11, 25],
    phase: -Math.PI / 2 + Math.PI / 12,
    color: "#ba91ff",
    start: 2.6,
    finish: 10.2,
  },
  {
    radii: [326, 64, 20],
    frequencies: [1, 13, -23],
    phase: -Math.PI / 2,
    color: "#79f5e7",
    start: 5.0,
    finish: 10.6,
  },
  {
    radii: [103, 64, 23],
    frequencies: [1, -11, 25],
    phase: -Math.PI / 2 + Math.PI / 12,
    color: "#eab5fa",
    start: 6.4,
    finish: 10.8,
  },
  {
    radii: [278, 48, 40],
    frequencies: [1, 25, -23],
    phase: -Math.PI / 2,
    color: "#ba91ff",
    start: 7.3,
    finish: 10.8,
  },
  {
    radii: [215, 36, 28],
    frequencies: [1, 25, -23],
    phase: -Math.PI / 2 + Math.PI / 12,
    color: "#79f5e7",
    start: 8.0,
    finish: 10.8,
  },
];
export const TAU = Math.PI * 2;
export const SAMPLES = 4800;
export const clamp = (v: number) => Math.min(1, Math.max(0, v));

export function chainAt(layer: Layer, turn: number): Point[] {
  const chain: Point[] = [{ x: 0, y: 0 }];
  layer.radii.forEach((radius, index) => {
    const previous = chain[chain.length - 1];
    const angle = TAU * turn * layer.frequencies[index] + layer.phase;
    chain.push({
      x: previous.x + radius * Math.cos(angle),
      y: previous.y + radius * Math.sin(angle),
    });
  });
  return chain;
}

export const tipAt = (layer: Layer, turn: number) =>
  chainAt(layer, turn)[layer.radii.length];

// Nonzero initial velocity, strictly increasing speed during drawing.
export function progressAt(layer: Layer, seconds: number): number {
  const u = clamp((seconds - layer.start) / (layer.finish - layer.start));
  return 0.16 * u + 0.84 * u * u;
}

// Continue tracing the already-completed curve in the final hold. Deceleration
// integrates to a continuous phase and preserves the exact tip/curve relation.
export function turnAt(layer: Layer, seconds: number): number {
  if (seconds <= layer.finish) return progressAt(layer, seconds);
  const t = seconds - layer.finish;
  const speed = 1.84 / (layer.finish - layer.start);
  return 1 + speed * 0.5 * (1 - Math.exp(-t / 0.5));
}

const xy = (p: Point) => `${p.x.toFixed(3)},${p.y.toFixed(3)}`;
const sampled = layers.map((layer) =>
  Array.from({ length: SAMPLES + 1 }, (_, i) => xy(tipAt(layer, i / SAMPLES))),
);

export function tracePath(index: number, progress: number): string {
  const p = clamp(progress);
  const count = Math.floor(p * SAMPLES);
  const prefix = sampled[index].slice(0, count + 1);
  // Append the analytic endpoint, not a rounded-down sample: no tip detachment.
  return `M${prefix.join("L")}L${xy(tipAt(layers[index], p))}`;
}

